import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, Icon } from 'semantic-ui-react';

import { useComponentOptions } from '../../../../config/design.context';
import { speakerPropTypes } from '../../../../propTypes';
import EntityLink, { usePageForEntity } from '../../../EntityLink';
import { EntityTags } from '../../../EntityTags';
import SquareImage from '../../../SquareImage';
import { SocialNetworks, cardMetaComponents } from '../components';
import VCardLink from '../components/VCardLink';

export const speakerThumbnailOptions = {
  type: 'auto', // Allow webp
};

const ClassicSpeakerCard = ({
  children,
  cdnOptions,
  extraFields,
  fluid,
  imageSize,
  ligthVariant,
  showLink,
  showModal,
  showMailBubble,
  speaker,
  tagFields,
  width,
  vcard,
  ...rest
}) => {
  const { t } = useTranslation();
  const { defaultThumbnail } = rest;
  const imageProps = useComponentOptions('thumbnails');
  const {
    company,
    shortDescription,
    firstName,
    lastName,
    role,
    thumbnail,
    email,
    socialNetworks,
    category,
  } = speaker;

  const page = usePageForEntity(speaker);
  const hasLink = page && showLink;

  return (
    <>
      <Card
        className="card--speaker"
        style={{ width }}
        fluid={fluid}
        as={hasLink || showModal ? EntityLink : undefined}
        entity={speaker}
        data-category={category || undefined}
      >
        {(thumbnail || defaultThumbnail) && (
          <div className="card__image">
            <SquareImage
              as="img"
              alt={`${firstName} ${lastName}`}
              src={thumbnail || defaultThumbnail}
              imageSize={imageSize || (ligthVariant ? 110 : 128)}
              {...imageProps}
              cdnOptions={{ ...imageProps?.cdnOptions, ...speakerThumbnailOptions, ...cdnOptions }}
              className={`avatar ${ligthVariant ? 'light' : ''}`}
            />

            {showMailBubble && email && (
              <a className="email bubble" href={`mailto:${email}`} target="_blank" rel="noreferrer">
                <Icon name="envelope outline" />
              </a>
            )}
          </div>
        )}

        <Card.Content className="content card__content">
          <Card.Header as="h2">
            {firstName} {lastName}
          </Card.Header>
          {company && <Card.Description>{company}</Card.Description>}
          {role && (
            <Card.Meta>
              <span className="date">{role} </span>
            </Card.Meta>
          )}
          {extraFields &&
            extraFields
              .filter((field) => speaker[field.dataKey])
              .map(({ lineType, dataKey, className, ...fieldProps }) => {
                const value = speaker[dataKey];
                const Component = cardMetaComponents[lineType] || Card.Meta;
                return (
                  <Component
                    key={dataKey}
                    className={`${className || ''} field-${dataKey}`}
                    {...fieldProps}
                    dataKey={dataKey}
                  >
                    {value}
                  </Component>
                );
              })}
          {shortDescription && !ligthVariant && (
            <Card.Description>{shortDescription}</Card.Description>
          )}
          <EntityTags tagFields={tagFields} entity={speaker} type="speaker" />
          {socialNetworks && !ligthVariant && <SocialNetworks socialNetworks={socialNetworks} />}
          {vcard?.enabled && <VCardLink user={speaker} config={vcard} />}
        </Card.Content>
        {hasLink && (
          <Card.Content className="navlink">
            <EntityLink autoHide entity={speaker}>
              {t('speakers.card.view-sessions')}
            </EntityLink>
          </Card.Content>
        )}
        {children}
      </Card>
    </>
  );
};

ClassicSpeakerCard.defaultProps = {
  children: undefined,
  cdnOptions: undefined,
  extraFields: [],
  fluid: undefined,
  imageSize: undefined,
  modalConfig: {},
  ligthVariant: false,
  showLink: true,
  showMailBubble: false,
  showModal: false,
  tagFields: [],
  vcard: undefined,
  width: undefined,
};

ClassicSpeakerCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  children: PropTypes.any,
  cdnOptions: PropTypes.object,
  extraFields: PropTypes.arrayOf(PropTypes.shape({ dataKey: PropTypes.string })),
  fluid: PropTypes.bool,
  imageSize: PropTypes.number,
  ligthVariant: PropTypes.bool,
  modalConfig: PropTypes.object,
  showLink: PropTypes.bool,
  showMailBubble: PropTypes.bool,
  showModal: PropTypes.bool,
  speaker: speakerPropTypes.isRequired,
  tagFields: PropTypes.arrayOf(PropTypes.string),
  vcard: PropTypes.shape({ enabled: PropTypes.bool }),
  width: PropTypes.number,
};

export default ClassicSpeakerCard;
